import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, deleteDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Plus, Calendar, Clock, User, Phone, MessageSquare, Trash2, Search, Dog, Edit3, ChevronLeft, ChevronRight, CreditCard, DollarSign, Car, MapPin, X } from 'lucide-react';
import CalendarView from '../components/Calendar/CalendarView';
import ConfirmationModal from '../components/ConfirmationModal';
import useConfirmation from '../hooks/useConfirmation';
import Portal from '../components/Portal';
import MenuDropdown from '../components/MenuDropdown';

const FORMA_PAGAMENTO_OPTIONS = [
  { value: '', label: 'Forma de pagamento' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'dinheiro', label: 'Dinheiro' }
];
import { useAuth } from '../contexts/AuthContext';
import { useTransactionTypes } from '../contexts/TransactionTypesContext';
import { useAppointmentsAggregate } from '../utils/useAppointmentsAggregate';
import { updateAppointmentsAggregationOnCreate, updateAppointmentsAggregationOnUpdate, updateAppointmentsAggregationOnDelete } from '../utils/appointmentsAggregations';
import { useTransactionsAggregate } from '../utils/useTransactionsAggregate';
import { usePetsAggregates } from '../utils/usePetsAggregates';
import { updateTransactionsAggregationOnCreate, updateTransactionsAggregationOnUpdate, updateTransactionsAggregationOnDelete } from '../utils/transactionsAggregations';

const Agenda = () => {
  // Usar agregado de pets diretamente
  const { pets, loading: petsLoading } = usePetsAggregates();
  const petMenuOptions = useMemo(
    () => [
      { value: '', label: petsLoading ? 'Carregando pets...' : 'Selecione um pet' },
      ...(petsLoading
        ? []
        : [...pets]
            .sort((a, b) =>
              (a.nome || '').toUpperCase().localeCompare((b.nome || '').toUpperCase(), 'pt-BR')
            )
            .map((pet) => ({
              value: pet.id,
              label: `${pet.nome} (${pet.tutor || 'Dono não informado'})`
            })))
    ],
    [pets, petsLoading]
  );
  const { transactionTypes } = useTransactionTypes();
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({
    idPet: '',
    tipoServico: '',
    dataAgendamento: '',
    horaAgendamento: '',
    precoServico: '',
    temTransporte: false,
    precoTransporte: '',
    formaPagamento: '',
    observacoes: '',
    agendamentoAvulso: false,
    nomePetAvulso: '',
    tutorAvulso: '',
    telefoneAvulso: '',
    racaAvulso: '',
    enderecoAvulso: ''
  });
  const [submittingAppointment, setSubmittingAppointment] = useState(false);
  const [appointmentFieldErrors, setAppointmentFieldErrors] = useState({});
  const [calendarAppointments, setCalendarAppointments] = useState([]);
  const [listAppointments, setListAppointments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const appointmentsPerPage = 10;
  const currentCalendarRangeRef = useRef(null);
  const appointmentModalSheetRef = useRef(null);
  const appointmentObservationsRef = useRef(null);
  const { confirmation, showConfirmation, hideConfirmation, confirmAction, cancelAction } = useConfirmation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Função helper para formatar telefone
  const formatPhone = (value) => {
    if (!value) return '';
    // Remove tudo que não é número
    const numbers = value.toString().replace(/\D/g, '');
    
    // Limita a 11 dígitos (máximo para celular brasileiro)
    const limitedNumbers = numbers.slice(0, 11);
    
    // Formata conforme o tamanho
    if (limitedNumbers.length <= 10) {
      // Telefone fixo: (XX) XXXX-XXXX
      return limitedNumbers.replace(/(\d{2})(\d{4})(\d{0,4})/, (match, p1, p2, p3) => {
        if (p3) return `(${p1}) ${p2}-${p3}`;
        if (p2) return `(${p1}) ${p2}`;
        if (p1) return `(${p1}`;
        return limitedNumbers;
      });
    } else {
      // Celular: (XX) XXXXX-XXXX
      return limitedNumbers.replace(/(\d{2})(\d{5})(\d{0,4})/, (match, p1, p2, p3) => {
        if (p3) return `(${p1}) ${p2}-${p3}`;
        if (p2) return `(${p1}) ${p2}`;
        if (p1) return `(${p1}`;
        return limitedNumbers;
      });
    }
  };

  // Usar agregado de agendamentos (atualização em tempo real)
  const { appointments: allAppointments, loading: appointmentsLoading, error: appointmentsError } = useAppointmentsAggregate();
  
  // Usar agregado de transações para evitar queries diretas (atualização em tempo real)
  const { transactions: allTransactions } = useTransactionsAggregate();

  // Pets e transactionTypes agora vêm dos contextos compartilhados

  // Carregar agendamentos das semanas visíveis no calendário (usando agregado)
  const handleMonthChange = React.useCallback((startDate, endDate, forceUpdate = false) => {
    if (appointmentsLoading) {
      setCalendarLoading(true);
      return;
    }

    // Usar as datas exatas das semanas visíveis
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    
    // Verificar se o range realmente mudou
    const rangeKey = `${start.getTime()}-${end.getTime()}`;
    const isFirstLoad = currentCalendarRangeRef.current === null;
    const rangeChanged = currentCalendarRangeRef.current !== rangeKey;
    
    if (!forceUpdate && !rangeChanged && !isFirstLoad) {
      // Range não mudou e não é a primeira carga, não precisa recarregar
      return;
    }
    
    currentCalendarRangeRef.current = rangeKey;
    setCalendarLoading(true);

    // Filtrar agendamentos do agregado por range de datas
    const filteredAppointments = allAppointments.filter(apt => {
      const aptDate = apt.dataAgendamento instanceof Date ? apt.dataAgendamento : new Date(apt.dataAgendamento);
      return aptDate >= start && aptDate <= end;
    });

    // Ordenar por data (ascendente para o calendário)
    filteredAppointments.sort((a, b) => {
      const dateA = a.dataAgendamento instanceof Date ? a.dataAgendamento : new Date(a.dataAgendamento);
      const dateB = b.dataAgendamento instanceof Date ? b.dataAgendamento : new Date(b.dataAgendamento);
      return dateA.getTime() - dateB.getTime();
    });
    
    setCalendarAppointments(filteredAppointments);
    setCalendarLoading(false);
  }, [allAppointments, appointmentsLoading]);

  // Garantir carregamento inicial quando o agregado terminar de carregar
  useEffect(() => {
    // Se o agregado terminou de carregar e ainda não há agendamentos no calendário
    if (!appointmentsLoading && currentCalendarRangeRef.current === null) {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Domingo
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 20); // 3 semanas
      endOfWeek.setHours(23, 59, 59, 999);
      
      handleMonthChange(startOfWeek, endOfWeek);
    }
  }, [appointmentsLoading, handleMonthChange]);

  // Atualizar calendário quando os agendamentos mudarem (após refresh)
  useEffect(() => {
    if (!appointmentsLoading && currentCalendarRangeRef.current) {
      const [startTime, endTime] = currentCalendarRangeRef.current.split('-').map(Number);
      const start = new Date(startTime);
      const end = new Date(endTime);
      // Forçar atualização mesmo se o range não mudou
      handleMonthChange(start, end, true);
    }
  }, [allAppointments.length, appointmentsLoading]);

  // Travar scroll do fundo quando o modal de agendamento estiver aberto
  useEffect(() => {
    if (showAppointmentModal) {
      const scrollY = window.scrollY;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      const prevBodyOverflow = document.body.style.overflow;
      const prevBodyPosition = document.body.style.position;
      const prevBodyTop = document.body.style.top;
      const prevBodyWidth = document.body.style.width;
      const prevBodyHeight = document.body.style.height;

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        document.body.style.position = prevBodyPosition;
        document.body.style.top = prevBodyTop;
        document.body.style.width = prevBodyWidth;
        document.body.style.height = prevBodyHeight;
        window.scrollTo(0, scrollY);
      };
    }
  }, [showAppointmentModal]);

  useLayoutEffect(() => {
    if (!showAppointmentModal) return;
    const el = appointmentObservationsRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    const onResize = () => {
      const node = appointmentObservationsRef.current;
      if (!node) return;
      node.style.height = 'auto';
      node.style.height = `${node.scrollHeight}px`;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showAppointmentModal, appointmentForm.observacoes]);

  // Processar e paginar dados do agregado para a lista
  useEffect(() => {
    if (appointmentsLoading) {
      setListLoading(true);
      return;
    }

    // Se houver erro, não processar
    if (appointmentsError) {
      setListLoading(false);
      return;
    }

    setListLoading(true);

    // Filtrar por termo de busca se houver (busca completa)
    let filtered = [...allAppointments];
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((appointment) => {
        const pet = pets.find(p => p.id === appointment.idPet);
        const nomePet = (pet?.nome || appointment.nomePet || '').toLowerCase();
        const ownerName = (appointment.ownernome || appointment.ownerName || '').toLowerCase();
        const phone = (appointment.telefone || '').toLowerCase();
        const tipoServico = (appointment.tipoServico || '').toLowerCase();
        const notes = (appointment.observacoes || '').toLowerCase();
        const status = (appointment.status || '').toLowerCase();
        
        // Formatações de data para busca
        const appointmentDate = appointment.dataAgendamento instanceof Date 
          ? appointment.dataAgendamento 
          : new Date(appointment.dataAgendamento);
        const dateStr = appointmentDate.toLocaleDateString('pt-BR').toLowerCase();
        const day = String(appointmentDate.getDate()).padStart(2, '0');
        const month = String(appointmentDate.getMonth() + 1).padStart(2, '0');
        const year = String(appointmentDate.getFullYear());
        const monthName = appointmentDate.toLocaleDateString('pt-BR', { month: 'long' }).toLowerCase();
        const weekday = appointmentDate.toLocaleDateString('pt-BR', { weekday: 'long' }).toLowerCase();
        
        // Formatações de hora
        const timeStr = appointmentDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }).toLowerCase();
        const hour = String(appointmentDate.getHours()).padStart(2, '0');
        const minute = String(appointmentDate.getMinutes()).padStart(2, '0');
        
        return nomePet.includes(searchLower) || 
               ownerName.includes(searchLower) || 
               phone.includes(searchLower) || 
               tipoServico.includes(searchLower) || 
               notes.includes(searchLower) ||
               status.includes(searchLower) ||
               dateStr.includes(searchLower) ||
               day.includes(searchLower) ||
               month.includes(searchLower) ||
               year.includes(searchLower) ||
               monthName.includes(searchLower) ||
               weekday.includes(searchLower) ||
               timeStr.includes(searchLower) ||
               hour.includes(searchLower) ||
               minute.includes(searchLower);
      });
    }

    // Ordenar por data (mais recente primeiro)
    filtered.sort((a, b) => {
      const dateA = a.dataAgendamento instanceof Date ? a.dataAgendamento : new Date(a.dataAgendamento);
      const dateB = b.dataAgendamento instanceof Date ? b.dataAgendamento : new Date(b.dataAgendamento);
      return dateB.getTime() - dateA.getTime();
    });

    // Paginação no agregado (client-side)
    const totalPages = Math.ceil(filtered.length / appointmentsPerPage);
    const startIndex = (currentPage - 1) * appointmentsPerPage;
    const endIndex = startIndex + appointmentsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    
    setListAppointments(paginated);
    setTotalFilteredCount(filtered.length);
    setHasNextPage(currentPage < totalPages);
    setHasPrevPage(currentPage > 1);
    setListLoading(false);
    
    // Resetar para página 1 se a busca mudou e não há resultados na página atual
    if (searchTerm && currentPage > 1 && paginated.length === 0) {
      setCurrentPage(1);
    }
  }, [allAppointments, appointmentsLoading, appointmentsError, searchTerm, currentPage, pets]);
  
  // Resetar página quando o termo de busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Funções de paginação simples
  const loadNextPage = () => {
    setCurrentPage(prev => prev + 1);
  };

  const loadPrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  // Atualizar placeholder do campo de pesquisa baseado no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      const searchInput = document.querySelector('input[data-desktop-placeholder]');
      if (searchInput) {
        if (window.innerWidth >= 640) {
          searchInput.placeholder = searchInput.getAttribute('data-desktop-placeholder');
        } else {
          searchInput.placeholder = 'Pesquisar';
        }
      }
    };

    handleResize(); // Executar na montagem
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      await showConfirmation({
        title: 'Erro de autenticação',
        message: 'Você precisa estar logado para salvar agendamentos.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
      return;
    }
    
    // Obrigatório no modal: Pet (normal) OU Nome do Pet (avulso)

    const errors = {};
    const selectedPetForValidation = appointmentForm.idPet
      ? pets.find((p) => p.id === appointmentForm.idPet)
      : null;

    if (appointmentForm.agendamentoAvulso) {
      if (!appointmentForm.nomePetAvulso?.trim()) errors.nomePetAvulso = true;
    } else {
      if (!appointmentForm.idPet) errors.idPet = true;
      else if (!selectedPetForValidation) errors.idPet = true;
    }

    if (Object.keys(errors).length > 0) {
      setAppointmentFieldErrors(errors);
      return;
    }
    setAppointmentFieldErrors({});

    // Validar valor total (serviço + transporte) se for maior que R$ 450
    const precoServico = parseFloat(appointmentForm.precoServico) || 0;
    const precoTransporte = (appointmentForm.temTransporte && appointmentForm.precoTransporte) ? parseFloat(appointmentForm.precoTransporte) : 0;
    const valorTotal = precoServico + precoTransporte;

    if (valorTotal > 450) {
      const valorFormatado = valorTotal.toLocaleString('pt-BR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
      
      const confirmed = await showConfirmation({
        title: 'Valor muito alto!',
        message: (
          <>
            <p>O valor total do agendamento é <strong>R$ {valorFormatado}</strong>.</p>
            <p>Este valor está correto?</p>
          </>
        ),
        confirmText: 'Sim, confirmar',
        cancelText: 'Cancelar',
        type: 'warning'
      });

      if (!confirmed) {
        return;
      }
    }

    setSubmittingAppointment(true);

    try {
      // Buscar pet do agregado (que agora inclui o telefone)
      const selectedPet = appointmentForm.idPet ? pets.find(p => p.id === appointmentForm.idPet) : null;
      
      if (!appointmentForm.agendamentoAvulso && !selectedPet) {
        await showConfirmation({
          title: 'Erro',
          message: 'Pet não encontrado. Por favor, selecione um pet válido.',
          confirmText: 'OK',
          showCancel: false,
          type: 'danger'
        });
        setSubmittingAppointment(false);
        return;
      }
      
      if (editingAppointment) {
        // Atualizar agendamento existente
        const appointmentRef = doc(db, 'agendamentos', editingAppointment.id);
        const scheduledDateTime = new Date(`${appointmentForm.dataAgendamento}T${appointmentForm.horaAgendamento}`);
        
        if (isNaN(scheduledDateTime.getTime())) {
          throw new Error('Data ou hora inválida');
        }
        
        await updateDoc(appointmentRef, {
          idPet: appointmentForm.idPet || '',
          nomePet: appointmentForm.agendamentoAvulso ? (appointmentForm.nomePetAvulso || '').toUpperCase() : (selectedPet?.nome || ''),
          ownernome: appointmentForm.agendamentoAvulso ? appointmentForm.tutorAvulso || '' : (selectedPet?.tutor || ''),
          telefone: appointmentForm.agendamentoAvulso ? appointmentForm.telefoneAvulso || '' : (selectedPet?.telefone || ''),
          raca: appointmentForm.agendamentoAvulso ? (appointmentForm.racaAvulso || '').toUpperCase() : '',
          endereco: appointmentForm.agendamentoAvulso ? (appointmentForm.enderecoAvulso || '').toUpperCase() : '',
          tipoServico: (appointmentForm.tipoServico || '').toUpperCase(),
          dataAgendamento: Timestamp.fromDate(scheduledDateTime),
          precoServico: appointmentForm.precoServico ? parseFloat(appointmentForm.precoServico) : 0,
          temTransporte: appointmentForm.temTransporte || false,
          precoTransporte: appointmentForm.temTransporte && appointmentForm.precoTransporte ? parseFloat(appointmentForm.precoTransporte) : 0,
          formaPagamento: appointmentForm.formaPagamento || '',
          observacoes: (appointmentForm.observacoes || '').toUpperCase(),
          agendamentoAvulso: appointmentForm.agendamentoAvulso || false,
          atualizadoEm: Timestamp.now(),
          atualizadoPor: {
            uid: user.uid,
            email: user.email || '',
            nomeExibicao: user.nomeExibicao || (user.email ? user.email.split('@')[0] : 'Usuário')
          }
        });

        // Otimização: usar dados que já temos ao invés de ler do Firestore
        const updatedAppointmentData = {
          id: editingAppointment.id,
          idPet: appointmentForm.idPet || '',
          nomePet: appointmentForm.agendamentoAvulso ? (appointmentForm.nomePetAvulso || '').toUpperCase() : (selectedPet?.nome || ''),
          ownernome: appointmentForm.agendamentoAvulso ? appointmentForm.tutorAvulso || '' : (selectedPet?.tutor || ''),
          telefone: appointmentForm.agendamentoAvulso ? appointmentForm.telefoneAvulso || '' : (selectedPet?.telefone || ''),
          raca: appointmentForm.agendamentoAvulso ? (appointmentForm.racaAvulso || '').toUpperCase() : '',
          endereco: appointmentForm.agendamentoAvulso ? (appointmentForm.enderecoAvulso || '').toUpperCase() : '',
          tipoServico: (appointmentForm.tipoServico || '').toUpperCase(),
          dataAgendamento: Timestamp.fromDate(scheduledDateTime),
          precoServico: appointmentForm.precoServico ? parseFloat(appointmentForm.precoServico) : 0,
          temTransporte: appointmentForm.temTransporte || false,
          precoTransporte: appointmentForm.temTransporte && appointmentForm.precoTransporte ? parseFloat(appointmentForm.precoTransporte) : 0,
          formaPagamento: appointmentForm.formaPagamento || '',
          observacoes: (appointmentForm.observacoes || '').toUpperCase(),
          agendamentoAvulso: appointmentForm.agendamentoAvulso || false,
          atualizadoEm: Timestamp.now(),
          atualizadoPor: {
            uid: user.uid,
            email: user.email || '',
            nomeExibicao: user.nomeExibicao || (user.email ? user.email.split('@')[0] : 'Usuário')
          },
          // Preservar campos do agendamento original
          status: editingAppointment.status || 'agendado',
          criadoEm: editingAppointment.criadoEm || Timestamp.now(),
          criadoPor: editingAppointment.criadoPor || null
        };
        
        // Preparar oldAppointmentData para comparação (usar editingAppointment original)
        const oldAppointmentData = {
          id: editingAppointment.id,
          dataAgendamento: editingAppointment.dataAgendamento
        };
        
        // Atualizar agregado de agendamentos
        try {
          await updateAppointmentsAggregationOnUpdate(oldAppointmentData, updatedAppointmentData);
        } catch (err) {
          // Erro ao atualizar agregado de agendamentos
        }
        
        // O listener em tempo real atualizará automaticamente

        // Buscar transação vinculada ao agendamento usando o agregado
        const linkedTransaction = allTransactions.find(t => (t.idAgendamento || t.appointmentId) === editingAppointment.id);

        if (appointmentForm.precoServico && parseFloat(appointmentForm.precoServico) > 0) {
          // Se houver transação vinculada, atualizar
          if (linkedTransaction) {
            const oldTransactionData = linkedTransaction;
            const nomeTipoTransacao = appointmentForm.agendamentoAvulso ? 'Agen. avulso' : 'Agendamentos';
            const newTransactionData = {
              tipo: 'receita',
              idPet: appointmentForm.idPet || '',
              valor: parseFloat(appointmentForm.precoServico),
              idTipoTransacao: '',
              descricao: (appointmentForm.observacoes || '').toUpperCase(),
              data: Timestamp.fromDate(scheduledDateTime),
              idAgendamento: editingAppointment.id,
              nomeTipoTransacao: nomeTipoTransacao
            };
            
            // Otimização: usar dados que já temos ao invés de ler do Firestore
            const nomePet = appointmentForm.agendamentoAvulso ? (appointmentForm.nomePetAvulso || '').toUpperCase() : (selectedPet?.nome || '');
            
            await updateDoc(doc(db, 'transacoes', linkedTransaction.id), {
              idPet: appointmentForm.idPet || '',
              valor: parseFloat(appointmentForm.precoServico),
              idTipoTransacao: '',
              nomeTipoTransacao: nomeTipoTransacao,
              descricao: (appointmentForm.observacoes || '').toUpperCase(),
              data: Timestamp.fromDate(scheduledDateTime),
              nomePet: nomePet || '',
              atualizadoEm: Timestamp.now()
            });
            
            const updatedTransactionData = {
              id: linkedTransaction.id,
              tipo: 'receita',
              idPet: appointmentForm.idPet || '',
              valor: parseFloat(appointmentForm.precoServico),
              idTipoTransacao: '',
              descricao: (appointmentForm.observacoes || '').toUpperCase(),
              data: Timestamp.fromDate(scheduledDateTime),
              idAgendamento: editingAppointment.id,
              atualizadoEm: Timestamp.now(),
              nomePet: nomePet,
              nomeTipoTransacao: nomeTipoTransacao,
              // Preservar campos da transação original
              criadoEm: linkedTransaction.criadoEm || Timestamp.now(),
              criadoPor: linkedTransaction.criadoPor || null
            };
            
            // Atualizar agregado de transações
            try {
              await updateTransactionsAggregationOnUpdate(linkedTransaction, updatedTransactionData);
            } catch (err) {
              // Erro ao atualizar agregado de transações
            }
            
            // O listener em tempo real atualizará automaticamente
          } else {
            // Se não houver transação vinculada, criar uma nova
            const nomeTipoTransacao = appointmentForm.agendamentoAvulso ? 'Agen. avulso' : 'Agendamentos';
            const nomePet = appointmentForm.agendamentoAvulso ? (appointmentForm.nomePetAvulso || '').toUpperCase() : (selectedPet?.nome || '');
            const transactionData = {
              tipo: 'receita',
              idPet: appointmentForm.idPet || '',
              valor: parseFloat(appointmentForm.precoServico),
              idTipoTransacao: '',
              nomeTipoTransacao: nomeTipoTransacao,
              descricao: (appointmentForm.observacoes || '').toUpperCase(),
              data: Timestamp.fromDate(scheduledDateTime),
              idAgendamento: editingAppointment.id,
              nomePet: nomePet || '',
              criadoEm: Timestamp.now(),
              criadoPor: {
                uid: user.uid,
                nomeExibicao: user.nomeExibicao || 'Usuário'
              }
            };

            const transactionDocRef = await addDoc(collection(db, 'transacoes'), transactionData);
            
            const newTransactionData = {
              id: transactionDocRef.id,
              tipo: 'receita',
              idPet: appointmentForm.idPet || '',
              valor: parseFloat(appointmentForm.precoServico),
              idTipoTransacao: '',
              nomeTipoTransacao: nomeTipoTransacao,
              descricao: (appointmentForm.observacoes || '').toUpperCase(),
              data: Timestamp.fromDate(scheduledDateTime),
              idAgendamento: editingAppointment.id,
              criadoEm: Timestamp.now(),
              criadoPor: {
                uid: user.uid,
                nomeExibicao: user.nomeExibicao || 'Usuário'
              },
              nomePet: nomePet
            };
            
            // Atualizar agregado de transações
            try {
              await updateTransactionsAggregationOnCreate(newTransactionData);
            } catch (err) {
              // Erro ao atualizar agregado de transações
            }
            
            // O listener em tempo real atualizará automaticamente
          }
        } else {
          // Se o preço foi removido, excluir a transação vinculada se existir
          if (linkedTransaction) {
            const transactionData = linkedTransaction;
            await deleteDoc(doc(db, 'transacoes', linkedTransaction.id));
            
            // Atualizar agregado de transações
            try {
              await updateTransactionsAggregationOnDelete(linkedTransaction.id, linkedTransaction.data || linkedTransaction.date);
            } catch (err) {
              // Erro ao atualizar agregado de transações
            }
            
            // O listener em tempo real atualizará automaticamente
          }
        }

        // Criar objeto de agendamento atualizado para atualizar os estados locais
        const updatedAppointment = {
          id: editingAppointment.id,
          idPet: appointmentForm.idPet || '',
          nomePet: appointmentForm.agendamentoAvulso ? (appointmentForm.nomePetAvulso || '').toUpperCase() : (selectedPet?.nome || ''),
          ownernome: appointmentForm.agendamentoAvulso ? appointmentForm.tutorAvulso || '' : (selectedPet?.tutor || ''),
          telefone: appointmentForm.agendamentoAvulso ? appointmentForm.telefoneAvulso || '' : (selectedPet?.telefone || ''),
          tipoServico: (appointmentForm.tipoServico || '').toUpperCase(),
          dataAgendamento: scheduledDateTime,
          precoServico: appointmentForm.precoServico ? parseFloat(appointmentForm.precoServico) : 0,
          temTransporte: appointmentForm.temTransporte || false,
          precoTransporte: appointmentForm.temTransporte && appointmentForm.precoTransporte ? parseFloat(appointmentForm.precoTransporte) : 0,
          formaPagamento: appointmentForm.formaPagamento || '',
          observacoes: (appointmentForm.observacoes || '').toUpperCase(),
          agendamentoAvulso: appointmentForm.agendamentoAvulso || false,
          status: editingAppointment.status || 'agendado',
          criadoEm: editingAppointment.criadoEm
        };
        
        // Atualizar o agendamento no estado do calendário
        setCalendarAppointments(prev => {
          const index = prev.findIndex(apt => apt.id === editingAppointment.id);
          if (index !== -1) {
            const updated = [...prev];
            updated[index] = updatedAppointment;
            // Reordenar por data
            return updated.sort((a, b) => {
              const dateA = a.dataAgendamento?.getTime?.() || new Date(a.dataAgendamento).getTime();
              const dateB = b.dataAgendamento?.getTime?.() || new Date(b.dataAgendamento).getTime();
              return dateA - dateB;
            });
          }
          // Se não estiver na lista do calendário, verificar se deve ser adicionado
          if (currentCalendarRangeRef.current) {
            const [startTime, endTime] = currentCalendarRangeRef.current.split('-').map(Number);
            const appointmentTime = scheduledDateTime.getTime();
            if (appointmentTime >= startTime && appointmentTime <= endTime) {
              const updated = [...prev, updatedAppointment];
              return updated.sort((a, b) => {
                const dateA = a.dataAgendamento?.getTime?.() || new Date(a.dataAgendamento).getTime();
                const dateB = b.dataAgendamento?.getTime?.() || new Date(b.dataAgendamento).getTime();
                return dateA - dateB;
              });
            }
          }
          return prev;
        });
        
        // Atualizar o agendamento na lista
        setListAppointments(prev => {
          const index = prev.findIndex(apt => apt.id === editingAppointment.id);
          if (index !== -1) {
            const updated = [...prev];
            updated[index] = updatedAppointment;
            // Reordenar por data (mais recentes primeiro)
            return updated.sort((a, b) => {
              const dateA = a.dataAgendamento?.getTime?.() || new Date(a.dataAgendamento).getTime();
              const dateB = b.dataAgendamento?.getTime?.() || new Date(b.dataAgendamento).getTime();
              return dateB - dateA;
            });
          }
          // Se não estiver na lista atual, pode precisar ser adicionado se estiver na primeira página
          // Mas como a lista é paginada, é melhor recarregar se o agendamento foi movido para outra página
          return prev;
        });
      } else {
        // Criar novo agendamento
        const scheduledDateTime = new Date(`${appointmentForm.dataAgendamento}T${appointmentForm.horaAgendamento}`);
        
        if (isNaN(scheduledDateTime.getTime())) {
          throw new Error('Data ou hora inválida');
        }
        
        const appointmentRecord = {
          idPet: appointmentForm.idPet || '',
          nomePet: appointmentForm.agendamentoAvulso ? (appointmentForm.nomePetAvulso || '').toUpperCase() : (selectedPet?.nome || ''),
          ownernome: appointmentForm.agendamentoAvulso ? appointmentForm.tutorAvulso || '' : (selectedPet?.tutor || ''),
          telefone: appointmentForm.agendamentoAvulso ? appointmentForm.telefoneAvulso || '' : (selectedPet?.telefone || ''),
          ...(appointmentForm.agendamentoAvulso && {
            raca: (appointmentForm.racaAvulso || '').toUpperCase(),
            endereco: (appointmentForm.enderecoAvulso || '').toUpperCase()
          }),
          tipoServico: (appointmentForm.tipoServico || '').toUpperCase(),
          dataAgendamento: Timestamp.fromDate(scheduledDateTime),
          precoServico: appointmentForm.precoServico ? parseFloat(appointmentForm.precoServico) : 0,
          temTransporte: appointmentForm.temTransporte || false,
          precoTransporte: appointmentForm.temTransporte && appointmentForm.precoTransporte ? parseFloat(appointmentForm.precoTransporte) : 0,
          formaPagamento: appointmentForm.formaPagamento || '',
          observacoes: (appointmentForm.observacoes || '').toUpperCase(),
          agendamentoAvulso: appointmentForm.agendamentoAvulso || false,
          status: 'agendado',
          criadoEm: Timestamp.now()
        };

        const appointmentDoc = await addDoc(collection(db, 'agendamentos'), appointmentRecord);
        
        // Otimização: usar dados que já temos ao invés de ler do Firestore
        const newAppointmentData = {
          id: appointmentDoc.id,
          ...appointmentRecord,
          status: 'agendado'
        };
        
        // Atualizar agregado de agendamentos
        try {
          await updateAppointmentsAggregationOnCreate(newAppointmentData);
        } catch (err) {
          // Erro ao atualizar agregado de agendamentos
        }
        
        // O listener em tempo real atualizará automaticamente

        // Se houver preço, criar transação de receita automaticamente
        if (appointmentForm.precoServico && parseFloat(appointmentForm.precoServico) > 0) {
          const nomeTipoTransacao = appointmentForm.agendamentoAvulso ? 'Agen. avulso' : 'Agendamentos';
          const nomePet = appointmentForm.agendamentoAvulso ? (appointmentForm.nomePetAvulso || '').toUpperCase() : (selectedPet?.nome || '');
          const transactionData = {
            tipo: 'receita',
            idPet: appointmentForm.idPet || '',
            valor: parseFloat(appointmentForm.precoServico),
            idTipoTransacao: '',
            nomeTipoTransacao: nomeTipoTransacao,
            descricao: appointmentForm.observacoes || '',
            data: Timestamp.fromDate(scheduledDateTime),
            idAgendamento: appointmentDoc.id,
            nomePet: nomePet || '',
            criadoEm: Timestamp.now(),
            criadoPor: {
              uid: user.uid,
              nomeExibicao: user.nomeExibicao || 'Usuário'
            }
          };

          const transactionDocRef = await addDoc(collection(db, 'transacoes'), transactionData);
          
          const newTransactionData = {
            id: transactionDocRef.id,
            tipo: 'receita',
            idPet: appointmentForm.idPet || '',
            valor: parseFloat(appointmentForm.precoServico),
            idTipoTransacao: '',
            nomeTipoTransacao: nomeTipoTransacao,
            descricao: appointmentForm.observacoes || '',
            data: Timestamp.fromDate(scheduledDateTime),
            idAgendamento: appointmentDoc.id,
            criadoEm: Timestamp.now(),
            criadoPor: {
              uid: user.uid,
              nomeExibicao: user.nomeExibicao || 'Usuário'
            },
            nomePet: nomePet
          };
          
          // Atualizar agregado de transações
          try {
            await updateTransactionsAggregationOnCreate(newTransactionData);
          } catch (err) {
            // Erro ao atualizar agregado de transações
          }
          
          // O listener em tempo real atualizará automaticamente

        }
      }
      
      // Limpar formulário
      setAppointmentForm({
        idPet: '',
        tipoServico: '',
        dataAgendamento: '',
        horaAgendamento: '',
        precoServico: '',
        temTransporte: false,
        precoTransporte: '',
        formaPagamento: '',
        observacoes: '',
        agendamentoAvulso: false,
        nomePetAvulso: '',
        tutorAvulso: '',
        telefoneAvulso: '',
        racaAvulso: '',
        enderecoAvulso: ''
      });
      setEditingAppointment(null);
      setShowAppointmentModal(false);
      
    } catch (error) {
      const errorMessage = error?.message || error?.toString() || 'Erro desconhecido';
      await showConfirmation({
        title: 'Erro',
        message: `Erro ao salvar agendamento: ${errorMessage}`,
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    } finally {
      setSubmittingAppointment(false);
    }
  };

  const handleAppointmentInputChange = (field, value) => {
    setAppointmentForm(prev => ({
      ...prev,
      [field]: value,
      // Se desmarcar o transporte, limpar o preço
      ...(field === 'temTransporte' && !value ? { precoTransporte: '' } : {})
    }));
    // Limpar erro do campo ao preencher
    if (appointmentFieldErrors[field] || (field === 'idPet' && appointmentFieldErrors.telefone)) {
      setAppointmentFieldErrors((prev) => {
        const next = { ...prev };
        if (next[field]) delete next[field];
        if (field === 'idPet' && next.telefone) delete next.telefone;
        return next;
      });
    }
  };

  const handleAppointmentClick = (appointment) => {
    if (appointment.idPet) {
      navigate(`/pets/${appointment.idPet}`);
    }
  };

  const handleEditAppointment = (appointment) => {
    let appointmentDate;
    if (appointment.dataAgendamento?.toDate) {
      appointmentDate = appointment.dataAgendamento.toDate();
    } else if (appointment.dataAgendamento instanceof Date) {
      appointmentDate = appointment.dataAgendamento;
    } else {
      appointmentDate = new Date(appointment.dataAgendamento);
    }
    
    const dateStr = appointmentDate.toISOString().split('T')[0];
    const timeStr = appointmentDate.toTimeString().slice(0, 5);
    
    setEditingAppointment(appointment);
    setAppointmentFieldErrors({});
    setAppointmentForm({
      idPet: appointment.idPet || '',
      tipoServico: (appointment.tipoServico || '').toUpperCase(),
      dataAgendamento: dateStr,
      horaAgendamento: timeStr,
      precoServico: (appointment.precoServico || appointment.servicePrice) ? (appointment.precoServico || appointment.servicePrice).toString() : '',
      temTransporte: (appointment.temTransporte || appointment.hasTransport) || ((appointment.precoTransporte || appointment.transportPrice) > 0),
      precoTransporte: (appointment.precoTransporte || appointment.transportPrice) ? (appointment.precoTransporte || appointment.transportPrice).toString() : '',
      formaPagamento: appointment.formaPagamento || appointment.paymentMethod || '',
      observacoes: (appointment.observacoes || '').toUpperCase(),
      agendamentoAvulso: appointment.agendamentoAvulso || false,
      nomePetAvulso: appointment.agendamentoAvulso ? (appointment.nomePet || '') : '',
      tutorAvulso: appointment.agendamentoAvulso ? (appointment.ownernome || appointment.ownerName || '') : '',
      telefoneAvulso: appointment.agendamentoAvulso ? (appointment.telefone || '') : '',
      racaAvulso: appointment.agendamentoAvulso ? (appointment.raca || '') : '',
      enderecoAvulso: appointment.agendamentoAvulso ? (appointment.endereco || '') : ''
    });
    setShowAppointmentModal(true);
  };

  const handleDeleteAppointment = async (appointment) => {
    // Verificar se há transação vinculada usando o agregado
    const linkedTransactions = allTransactions.filter(t => (t.idAgendamento || t.appointmentId) === appointment.id);
    const hasTransaction = linkedTransactions.length > 0;

    const precoServico = appointment.precoServico || appointment.servicePrice || 0;
    const message = hasTransaction 
      ? `Tem certeza que deseja excluir o agendamento de ${appointment.nomePet || 'pet'} para ${appointment.tipoServico || 'serviço'}? A transação de agendamento vinculada (R$ ${precoServico?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}) também será excluída. Esta ação não pode ser desfeita.`
      : `Tem certeza que deseja excluir o agendamento de ${appointment.nomePet || 'pet'} para ${appointment.tipoServico || 'serviço'}? Esta ação não pode ser desfeita.`;

    const confirmed = await showConfirmation({
      title: 'Excluir Agendamento',
        message: message,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      // Excluir todas as transações vinculadas ao agendamento
      if (hasTransaction) {
        try {
          const deleteTransactionPromises = linkedTransactions.map(async (transaction) => {
            const transactionData = transaction;
            await deleteDoc(doc(db, 'transacoes', transaction.id));
            // Atualizar agregado de transações
            try {
              await updateTransactionsAggregationOnDelete(transaction.id, transaction.data || transaction.date);
            } catch (err) {
              // Erro ao atualizar agregado de transações
            }
          });
          await Promise.all(deleteTransactionPromises);
          
          // O listener em tempo real atualizará automaticamente
          
        } catch (transactionError) {
          throw transactionError;
        }
      }

      // Excluir o agendamento
      await deleteDoc(doc(db, 'agendamentos', appointment.id));

      // Atualizar agregado de agendamentos
      try {
        await updateAppointmentsAggregationOnDelete(appointment.id, appointment.dataAgendamento);
      } catch (err) {
        // Erro ao atualizar agregado de agendamentos
      }
      
      // Atualizar estado local imediatamente para feedback visual
      setCalendarAppointments(prev => prev.filter(apt => apt.id !== appointment.id));
      setListAppointments(prev => prev.filter(apt => apt.id !== appointment.id));
      
      // O listener em tempo real atualizará automaticamente
      
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        message: `Ocorreu um erro ao excluir o agendamento: ${error.message || 'Erro desconhecido'}. Por favor, tente novamente.`,
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    }
  };

  const getServiceTypeLabel = (type) => {
    return type || 'Serviço não informado';
  };

  // Função para extrair apenas o primeiro nome com apenas a primeira letra maiúscula
  const getFirstName = (fullName) => {
    if (!fullName) return '';
    const firstName = fullName.trim().split(' ')[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  };


  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-foreground-light dark:text-foreground-dark flex items-center space-x-2">
            <Calendar className="w-6 h-6" />
            <span>Agenda</span>
          </h1>
          <p className="hidden sm:block text-muted-light dark:text-muted-dark">Gerencie agendamentos e visualize sua agenda</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              setAppointmentFieldErrors({});
              setShowAppointmentModal(true);
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Novo Agendamento</span>
            <span className="sm:hidden">Novo</span>
          </button>
        </div>
      </div>


      {/* Calendário e Lista de Agendamentos */}
      <div className="space-y-4 w-full max-w-full overflow-x-hidden">
        <CalendarView 
          appointments={calendarAppointments}
          loading={calendarLoading}
          onMonthChange={handleMonthChange}
          onEditAppointment={handleEditAppointment}
          onDeleteAppointment={handleDeleteAppointment}
          key="calendar-view"
        />
      </div>

      {/* Linha Separadora */}
      <div className="border-t border-border-light dark:border-border-dark my-6"></div>

      {/* Todos os Agendamentos */}
      <div className="bg-card-light dark:bg-card-dark rounded-lg border border-border-light dark:border-border-dark w-full max-w-full overflow-x-hidden">
        <div className="p-3 sm:p-4 border-b border-border-light dark:border-border-dark">
          <div className="mb-3 sm:mb-4 flex justify-between items-center">
            <h2 className="text-base sm:text-lg font-semibold text-foreground-light dark:text-foreground-dark flex items-center space-x-2">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Todos os Agendamentos</span>
            </h2>
          </div>

          {/* Barra de Pesquisa */}
          <div className="relative">
            <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-light dark:text-muted-dark" />
            <input
              type="text"
              placeholder="Pesquisar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-8 sm:pl-10 w-full text-sm sm:text-base"
              data-desktop-placeholder="Pesquisar por pet, tutor, telefone, serviço, status, data (DD/MM/YYYY, DD/MM, DD-MM-YYYY)..."
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-muted-light dark:text-muted-dark hover:text-foreground-light dark:hover:text-foreground-dark transition-colors"
                title="Limpar pesquisa"
              >
                ×
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="mt-2 text-xs sm:text-sm text-muted-light dark:text-muted-dark">
              {listAppointments.length} agendamento{listAppointments.length !== 1 ? 's' : ''} encontrado{listAppointments.length !== 1 ? 's' : ''}
            </div>
          )}

        </div>

        <div className="p-3 sm:p-4 min-w-0 overflow-x-hidden">
          {listLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : listAppointments.length === 0 ? (
            <div className="text-center text-muted-light dark:text-muted-dark py-6 sm:py-8">
              <Calendar className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 opacity-50" />
              <p className="text-base sm:text-lg font-medium">Nenhum agendamento</p>
              <p className="text-xs sm:text-sm">Não há agendamentos cadastrados</p>
            </div>
          ) : listAppointments.length === 0 ? (
            <div className="text-center text-muted-light dark:text-muted-dark py-6 sm:py-8">
              <Search className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 opacity-50" />
              <p className="text-base sm:text-lg font-medium">Nenhum agendamento encontrado</p>
              <p className="text-xs sm:text-sm">Tente pesquisar com outros termos</p>
              <button
                onClick={() => setSearchTerm('')}
                className="btn-secondary mt-3 sm:mt-4 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
              >
                Limpar Pesquisa
              </button>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3 min-w-0">
              {listAppointments.map((appointment, index) => {
                const appointmentDate = new Date(appointment.dataAgendamento);
                const appointmentDatePlusOneHour = new Date(appointmentDate.getTime() + 60 * 60 * 1000);
                const now = new Date();
                const isPast = appointmentDatePlusOneHour < now;
                const shouldShowAgendado = appointment.status === 'agendado' && !isPast;
                const statusBadge = (!shouldShowAgendado && appointment.status === 'agendado') ? null : (
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                    appointment.status === 'agendado' 
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                      : appointment.status === 'concluido'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                  }`}>
                    {appointment.status === 'agendado' ? 'Agendado' : 
                     appointment.status === 'concluido' ? 'Concluído' : 'Em andamento'}
                  </span>
                );

                return (
                <div
                  key={appointment.id || index}
                  className="bg-background-light dark:bg-background-dark rounded-lg p-3 sm:p-4 border border-border-light dark:border-border-dark hover:shadow-md transition-all duration-200 overflow-hidden min-w-0"
                >
                  {/* Mobile: data/hora + badge + botões em uma linha */}
                  <div className="flex sm:hidden justify-between items-center gap-2 mb-2 flex-shrink-0">
                    <div className="flex items-center space-x-1 text-primary-600 dark:text-primary-400 min-w-0">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span className="text-xs font-medium truncate">
                        {appointmentDate.toLocaleDateString('pt-BR')} às {appointmentDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {appointment.agendamentoAvulso && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                          Avulso
                        </span>
                      )}
                      {statusBadge}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditAppointment(appointment);
                        }}
                        className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all duration-200"
                        title="Editar agendamento"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAppointment(appointment);
                        }}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                        title="Excluir agendamento"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Desktop: layout original */}
                  <div className="flex items-start justify-between">
                    <div 
                      onClick={() => handleAppointmentClick(appointment)}
                      className={`flex flex-wrap items-start gap-3 sm:gap-5 flex-1 min-w-0 ${
                        appointment.idPet ? 'cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/10 rounded-lg p-2 -m-2' : 'cursor-default'
                      }`}
                    >
                      <div className="hidden sm:flex items-center space-x-2 sm:space-x-3">
                        <div className="flex items-center space-x-1 sm:space-x-2 text-primary-600 dark:text-primary-400">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="text-xs sm:text-sm font-medium">
                            {new Date(appointment.dataAgendamento).toLocaleDateString('pt-BR')} às {new Date(appointment.dataAgendamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        {appointment.nomePet && (
                          <div className="flex items-center space-x-1">
                            <Dog className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                            <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{appointment.nomePet}</span>
                          </div>
                        )}
                      </div>
                      {/* Mobile: pet e demais info (sem data/hora); Desktop: continua no fluxo */}
                      <div className="flex sm:hidden flex-wrap items-start gap-2">
                        {appointment.nomePet && (
                          <div className="flex items-center space-x-1">
                            <Dog className="w-3 h-3 text-muted-light dark:text-muted-dark" />
                            <span className="text-xs text-foreground-light dark:text-foreground-dark">{appointment.nomePet}</span>
                          </div>
                        )}
                      </div>
                      
                      {(((appointment.precoServico ?? appointment.servicePrice ?? 0) > 0) || appointment.eRecorrente) && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                          <div className="flex items-center space-x-1" title="Preço do serviço">
                            <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-success-600 dark:text-success-400" />
                            <span className="text-xs sm:text-sm font-semibold text-success-600 dark:text-success-400">
                              {(appointment.precoServico ?? appointment.servicePrice ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          {((appointment.temTransporte || appointment.hasTransport) && ((appointment.precoTransporte || appointment.transportPrice) > 0)) && (
                            <>
                              <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-foreground-light dark:text-foreground-dark" />
                              <div className="flex items-center" title="Preço do transporte">
                                <Car className="w-3 h-3 sm:w-4 sm:h-4 text-primary-600 dark:text-primary-400" />
                                <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-primary-600 dark:text-primary-400 -ml-0.5" />
                                <span className="text-xs sm:text-sm font-semibold text-primary-600 dark:text-primary-400 ml-1">
                                  {(appointment.precoTransporte || appointment.transportPrice || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      
                      {(appointment.formaPagamento || appointment.paymentMethod) && (
                        <div className="flex items-center space-x-1">
                          <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                          <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                            {(() => {
                              const formaPagamento = appointment.formaPagamento || appointment.paymentMethod;
                              return formaPagamento === 'pix' ? 'PIX' : 
                                     formaPagamento === 'cartao' ? 'Cartão' : 
                                     formaPagamento === 'dinheiro' ? 'Dinheiro' : formaPagamento;
                            })()}
                          </span>
                        </div>
                      )}
                      
                      {(appointment.ownernome || appointment.ownerName) && (
                        <div className="flex items-center space-x-1">
                          <User className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                          <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">{getFirstName(appointment.ownernome || appointment.ownerName)}</span>
                        </div>
                      )}
                      
                      {appointment.telefone ? (
                        <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1 min-w-0">
                          <div className="flex items-center space-x-1 min-w-0">
                            <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark flex-shrink-0" />
                            <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark truncate">
                              {formatPhone(appointment.telefone)}
                            </span>
                          </div>
                          <span className="text-sm sm:text-base font-semibold text-foreground-light dark:text-foreground-dark break-words min-w-0">
                            • {getServiceTypeLabel(appointment.tipoServico)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm sm:text-base font-semibold text-foreground-light dark:text-foreground-dark break-words min-w-0 block">
                          • {getServiceTypeLabel(appointment.tipoServico)}
                        </span>
                      )}
                      
                      {(appointment.agendamentoAvulso && (appointment.raca || appointment.endereco)) || appointment.observacoes ? (
                        <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1 w-full min-w-0 order-last">
                          {appointment.agendamentoAvulso && appointment.raca && (
                            <div className="flex items-center space-x-1">
                              <Dog className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                              <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                                Raça: {appointment.raca}
                              </span>
                            </div>
                          )}
                          
                          {appointment.agendamentoAvulso && appointment.endereco && (
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark" />
                              <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark">
                                {appointment.endereco}
                              </span>
                            </div>
                          )}
                          
                          {appointment.observacoes && (
                            <div className="flex items-start space-x-1 min-w-0">
                              <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-muted-light dark:text-muted-dark mt-0.5 flex-shrink-0" />
                              <span className="text-xs sm:text-sm text-foreground-light dark:text-foreground-dark leading-relaxed break-words min-w-0">
                                {appointment.observacoes}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : null}

                    </div>
                    <div className="hidden sm:flex items-center space-x-1 sm:space-x-2">
                      {appointment.agendamentoAvulso && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                          Avulso
                        </span>
                      )}
                      {statusBadge}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditAppointment(appointment);
                        }}
                        className="p-1.5 sm:p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all duration-200"
                        title="Editar agendamento"
                      >
                        <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAppointment(appointment);
                        }}
                        className="p-1.5 sm:p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                        title="Excluir agendamento"
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
          
          {/* Controles de Paginação */}
          {(!listLoading && totalFilteredCount > appointmentsPerPage && (hasNextPage || hasPrevPage || currentPage > 1)) && (
            <div className="flex items-center justify-center mt-6 pt-4 border-t border-border-light dark:border-border-dark">
              <div className="flex items-center space-x-2">
                <button
                  onClick={loadPrevPage}
                  disabled={!hasPrevPage || listLoading}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    hasPrevPage && !listLoading
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Anterior</span>
                </button>
                
                <span className="text-sm text-muted-light dark:text-muted-dark">
                  Página {currentPage} de {Math.ceil(totalFilteredCount / appointmentsPerPage)}
                </span>
                
                <button
                  onClick={loadNextPage}
                  disabled={!hasNextPage || listLoading}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    hasNextPage && !listLoading
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <span>Próxima</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Novo Agendamento (layout alinhado ao modal Agendar Serviço / WhatsApp) */}
      {showAppointmentModal && (
        <Portal>
          <div 
            className="fixed inset-0 z-[99999] flex items-stretch justify-center bg-black bg-opacity-50 p-0 sm:items-center sm:p-4"
            data-modal="appointment"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setAppointmentFieldErrors({});
                setShowAppointmentModal(false);
                setEditingAppointment(null);
                setAppointmentForm({
                  idPet: '',
                  tipoServico: '',
                  dataAgendamento: '',
                  horaAgendamento: '',
                  precoServico: '',
                  temTransporte: false,
                  precoTransporte: '',
                  formaPagamento: '',
                  observacoes: '',
                  agendamentoAvulso: false,
                  nomePetAvulso: '',
                  tutorAvulso: '',
                  telefoneAvulso: '',
                  racaAvulso: '',
                  enderecoAvulso: ''
                });
              }
            }}
          >
            <div
              ref={appointmentModalSheetRef}
              className="relative flex h-dvh max-h-dvh min-h-0 w-full flex-col overflow-hidden bg-card-light dark:bg-card-dark sm:h-auto sm:min-h-0 sm:max-h-[90vh] sm:max-w-md sm:rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative z-50 flex shrink-0 items-center justify-between gap-2 border-b border-border-light bg-card-light px-4 py-4 dark:border-border-dark dark:bg-card-dark sm:px-6">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {editingAppointment ? <Edit3 className="h-5 w-5 shrink-0" /> : <Plus className="h-5 w-5 shrink-0" />}
                  <h2 className="min-w-0 text-xl font-bold text-foreground-light dark:text-foreground-dark">
                    {editingAppointment ? 'Editar Agendamento' : 'Novo Agendamento'}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAppointmentFieldErrors({});
                    setShowAppointmentModal(false);
                    setEditingAppointment(null);
                    setAppointmentForm({
                      idPet: '',
                      tipoServico: '',
                      dataAgendamento: '',
                      horaAgendamento: '',
                      precoServico: '',
                      temTransporte: false,
                      precoTransporte: '',
                      formaPagamento: '',
                      observacoes: '',
                      agendamentoAvulso: false,
                      nomePetAvulso: '',
                      tutorAvulso: '',
                      telefoneAvulso: '',
                      racaAvulso: '',
                      enderecoAvulso: ''
                    });
                  }}
                  className="shrink-0 p-1 text-muted-light transition-colors hover:text-foreground-light dark:text-muted-dark dark:hover:text-foreground-dark"
                  aria-label="Fechar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form 
                onSubmit={(e) => {
                  handleAppointmentSubmit(e);
                }} 
                className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6"
                style={{ overscrollBehavior: 'contain' }}
                noValidate
              >
                <div>
                  <span id="agenda-idPet-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    Pet
                  </span>
                  <MenuDropdown
                    id="agenda-idPet"
                    name="idPet"
                    value={appointmentForm.idPet}
                    onChange={(e) => handleAppointmentInputChange('idPet', e.target.value)}
                    options={petMenuOptions}
                    placeholder="Selecione um pet"
                    required={!appointmentForm.agendamentoAvulso}
                    disabled={petsLoading || appointmentForm.agendamentoAvulso}
                    portalRootRef={appointmentModalSheetRef}
                    ariaLabelledBy="agenda-idPet-caption"
                    className={`${(petsLoading || appointmentForm.agendamentoAvulso) ? 'opacity-50' : ''} ${appointmentFieldErrors.idPet ? '!border-red-500' : ''}`}
                  />
                  {appointmentFieldErrors.idPet && (
                    <span className="text-red-500 text-xs mt-1 block">Preencha este campo</span>
                  )}
                  {appointmentFieldErrors.telefone && (
                    <span className="text-red-500 text-xs mt-1 block">
                      O pet precisa ter telefone cadastrado (mín. 10 dígitos). Atualize o cadastro do pet ou escolha outro.
                    </span>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={appointmentForm.agendamentoAvulso}
                      onChange={(e) => {
                        handleAppointmentInputChange('agendamentoAvulso', e.target.checked);
                        if (e.target.checked) {
                          handleAppointmentInputChange('idPet', '');
                        } else {
                          handleAppointmentInputChange('nomePetAvulso', '');
                          handleAppointmentInputChange('tutorAvulso', '');
                          handleAppointmentInputChange('telefoneAvulso', '');
                          handleAppointmentInputChange('racaAvulso', '');
                          handleAppointmentInputChange('enderecoAvulso', '');
                        }
                      }}
                      aria-label="Agendamento avulso"
                      className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Agendamento avulso</span>
                  </div>
                </div>

                {appointmentForm.agendamentoAvulso && (
                  <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <span id="agenda-nomePetAvulso-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                          Nome do Pet
                        </span>
                        <input
                          type="text"
                          value={appointmentForm.nomePetAvulso}
                          onChange={(e) => handleAppointmentInputChange('nomePetAvulso', e.target.value.toUpperCase())}
                          aria-labelledby="agenda-nomePetAvulso-caption"
                          className={`input-field !py-1.5 ${appointmentFieldErrors.nomePetAvulso ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`}
                          placeholder="Ex: REX"
                        />
                        {appointmentFieldErrors.nomePetAvulso && (
                          <span className="text-red-500 text-xs mt-1 block">Preencha este campo</span>
                        )}
                      </div>
                      <div>
                        <span id="agenda-racaAvulso-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                          Raça
                        </span>
                        <input
                          type="text"
                          value={appointmentForm.racaAvulso}
                          onChange={(e) => handleAppointmentInputChange('racaAvulso', e.target.value.toUpperCase())}
                          aria-labelledby="agenda-racaAvulso-caption"
                          className="input-field !py-1.5"
                          placeholder="Ex: GOLDEN"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <span id="agenda-tutorAvulso-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                          Tutor
                        </span>
                        <input
                          type="text"
                          value={appointmentForm.tutorAvulso}
                          onChange={(e) => {
                            const processedValue = e.target.value.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g, '');
                            handleAppointmentInputChange('tutorAvulso', processedValue.toUpperCase());
                          }}
                          aria-labelledby="agenda-tutorAvulso-caption"
                          className="input-field !py-1.5 uppercase"
                          placeholder="Ex: JOÃO SILVA"
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>

                      <div>
                        <span id="agenda-telefoneAvulso-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                          Telefone
                        </span>
                        <input
                          type="text"
                          value={appointmentForm.telefoneAvulso}
                          onChange={(e) => {
                            const formatted = formatPhone(e.target.value);
                            handleAppointmentInputChange('telefoneAvulso', formatted);
                          }}
                          aria-labelledby="agenda-telefoneAvulso-caption"
                          className={`input-field !py-1.5 ${appointmentFieldErrors.telefoneAvulso ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`}
                          placeholder="(00) 00000-0000"
                        />
                        {appointmentFieldErrors.telefoneAvulso && (
                          <span className="text-red-500 text-xs mt-1 block">Informe um telefone válido (mín. 10 dígitos)</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <span id="agenda-enderecoAvulso-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                        Endereço
                      </span>
                      <input
                        type="text"
                        value={appointmentForm.enderecoAvulso}
                        onChange={(e) => handleAppointmentInputChange('enderecoAvulso', e.target.value.toUpperCase())}
                        aria-labelledby="agenda-enderecoAvulso-caption"
                        className="input-field !py-1.5"
                        placeholder="Ex: RUA EXEMPLO, 123 - BAIRRO"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <span id="agenda-tipoServico-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    Tipo de Serviço
                  </span>
                  <input
                    type="text"
                    value={appointmentForm.tipoServico}
                    onChange={(e) => handleAppointmentInputChange('tipoServico', e.target.value.toUpperCase())}
                    aria-labelledby="agenda-tipoServico-caption"
                    className={`input-field !py-1.5 ${appointmentFieldErrors.tipoServico ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Ex: BANHO, TOSA, BANHO + TOSA, etc."
                  />
                  {appointmentFieldErrors.tipoServico && (
                    <span className="text-red-500 text-xs mt-1 block">Preencha este campo</span>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <span id="agenda-precoServico-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Preço do Serviço (R$)
                    </span>
                    <input
                      type="text"
                      value={appointmentForm.precoServico}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 
                          ? parts[0] + '.' + parts.slice(1).join('')
                          : value;
                        handleAppointmentInputChange('precoServico', filteredValue);
                      }}
                      aria-labelledby="agenda-precoServico-caption"
                      className={`input-field !py-1.5 ${appointmentFieldErrors.precoServico ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="0.00"
                      inputMode="decimal"
                    />
                    {appointmentFieldErrors.precoServico && (
                      <span className="text-red-500 text-xs mt-1 block">Preencha com um valor maior que zero</span>
                    )}
                  </div>

                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={appointmentForm.temTransporte}
                        onChange={(e) => {
                          handleAppointmentInputChange('temTransporte', e.target.checked);
                          if (!e.target.checked) {
                            handleAppointmentInputChange('precoTransporte', '');
                          }
                        }}
                        aria-label="Incluir transporte (R$)"
                        className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Incluir transporte (R$)</span>
                    </div>
                    {appointmentForm.temTransporte && (
                      <>
                        <input
                          type="text"
                          value={appointmentForm.precoTransporte}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            const parts = value.split('.');
                            const filteredValue = parts.length > 2 
                              ? parts[0] + '.' + parts.slice(1).join('')
                              : value;
                            handleAppointmentInputChange('precoTransporte', filteredValue);
                          }}
                          className="input-field !py-1.5"
                          placeholder="0.00"
                          inputMode="decimal"
                          aria-label="Preço do transporte em reais"
                        />
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <span id="agenda-formaPagamento-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    Forma de Pagamento
                  </span>
                  <MenuDropdown
                    id="agenda-formaPagamento"
                    name="formaPagamento"
                    value={appointmentForm.formaPagamento}
                    onChange={(e) => handleAppointmentInputChange('formaPagamento', e.target.value)}
                    options={FORMA_PAGAMENTO_OPTIONS}
                    placeholder="Forma de pagamento"
                    portalRootRef={appointmentModalSheetRef}
                    ariaLabelledBy="agenda-formaPagamento-caption"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <span id="agenda-dataAgendamento-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Data
                    </span>
                    <input
                      type="date"
                      value={appointmentForm.dataAgendamento}
                      onChange={(e) => handleAppointmentInputChange('dataAgendamento', e.target.value)}
                      aria-labelledby="agenda-dataAgendamento-caption"
                      className={`input-field !py-1.5 ${appointmentFieldErrors.dataAgendamento ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {appointmentFieldErrors.dataAgendamento && (
                      <span className="text-red-500 text-xs mt-1 block">Preencha este campo</span>
                    )}
                  </div>

                  <div>
                    <span id="agenda-horaAgendamento-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Hora
                    </span>
                    <input
                      type="time"
                      value={appointmentForm.horaAgendamento}
                      onChange={(e) => handleAppointmentInputChange('horaAgendamento', e.target.value)}
                      aria-labelledby="agenda-horaAgendamento-caption"
                      className={`input-field !py-1.5 ${appointmentFieldErrors.horaAgendamento ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {appointmentFieldErrors.horaAgendamento && (
                      <span className="text-red-500 text-xs mt-1 block">Preencha este campo</span>
                    )}
                  </div>
                </div>

                <div>
                  <span id="agenda-observacoes-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    Observações
                  </span>
                  <textarea
                    ref={appointmentObservationsRef}
                    value={appointmentForm.observacoes}
                    onChange={(e) => handleAppointmentInputChange('observacoes', e.target.value.toUpperCase())}
                    aria-labelledby="agenda-observacoes-caption"
                    className="input-field !py-1.5 min-h-[4.5rem] resize-none overflow-hidden"
                    placeholder="OBSERVAÇÕES ADICIONAIS..."
                    rows={1}
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setAppointmentFieldErrors({});
                      setShowAppointmentModal(false);
                      setEditingAppointment(null);
                      setAppointmentForm({
                        idPet: '',
                        tipoServico: '',
                        dataAgendamento: '',
                        horaAgendamento: '',
                        precoServico: '',
                        temTransporte: false,
                        precoTransporte: '',
                        formaPagamento: '',
                        observacoes: '',
                        agendamentoAvulso: false,
                        nomePetAvulso: '',
                        tutorAvulso: '',
                        telefoneAvulso: '',
                        racaAvulso: '',
                        enderecoAvulso: ''
                      });
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={submittingAppointment}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await handleAppointmentSubmit(e);
                    }}
                    className="flex-1 btn-primary"
                  >
                    {submittingAppointment ? (editingAppointment ? 'Salvando...' : 'Agendando...') : (editingAppointment ? 'Salvar' : 'Agendar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Portal>
      )}

      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onClose={hideConfirmation}
        onConfirm={confirmAction}
        onCancel={cancelAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
      />
    </div>
  );
};

export default Agenda;
